import { ScheduledMessage } from '@chewy/loyalty-contextual-message';

import { QuotesResponse } from '@/components/sign-up/CustomerQuotesCarousel/CustomerQuotesCarousel';
import { HolidaySignupContent } from '@/hooks/useShouldDisplayHolidaySection';

export interface FeatureConfig {
  'cancellation-survey-widget-enabled'?: boolean;
  'feature.pet-portal-redirect.enabled'?: boolean;
  'feature.holiday-offer.enabled'?: boolean;
  'feature.content-mfe.enabled'?: boolean;
  'customer-quotes'?: QuotesResponse;
  'feature.pet-birthday-offer-code'?: string;
  'feature.header-footer.enabled.chewy-portal'?: boolean;
  'feature.rewards-cap.enabled'?: boolean;
  POSTAL_CHECK_DELIVERY_AREA?: string;
  'feature.page-animation.type'?: string;
  'feature.signup.native-sheets.enabled'?: boolean;
  'feature.auto-renew.native-sheets.enabled'?: boolean;
  'feature.expiring-rewards.enabled'?: boolean;
  'feature.terms-and-conditions.native-sheet.enabled'?: boolean;
  'feature.terms-and-conditions.kib-sheet.enabled'?: boolean;
  'feature.membership.suspended.cancel.enabled'?: boolean;
  'feature.savings-and-rewards.native-sheets.enabled'?: boolean;
  'feature.manage-membership.native-sheet.enabled'?: boolean;
  'feature.manage-membership.bpm-disclaimer.enabled'?: boolean;
  'feature.native-sheets.android-version-for-scrolling'?: string;
  'feature.native-sheets.android-version-manage-membership-sheet'?: string;
  'feature.updated-price.enabled'?: boolean;
  'feature.vwo.enabled'?: boolean;

  'feature.widget-scheduled-messages.enabled'?: boolean;
  'feature.widget-scheduled-messages.config'?: { scheduledMessages?: ScheduledMessage[] };
  'feature.pet-profile-carousel.autoplay.enabled'?: boolean;
  'feature.holiday-2025-border.enabled'?: boolean;
  'feature.updated-theme.enabled'?: boolean;
  'feature.updated-theme.config'?: { startTimestamp?: string; endTimestamp?: string };
  'feature.sign-up.holiday-signup.content'?: { holidaySignupContent?: HolidaySignupContent[] };
  'feature.revise-membership-order-payment-flow.enabled'?: boolean;
  'feature.holiday-greeting.native-sheet.enabled'?: boolean;
  'feature.sign-up.v3.enabled'?: boolean;
  'feature.membership-dashboard.v3.enabled'?: boolean;
  'feature.cms-zone-light-mode-lock-workaround.enabled'?: boolean;
  'feature.upsell-sheet.stepped-terms-and-conditions.enabled'?: boolean;
  'feature.upsell-sheet.apple-pay-and-google-pay.disabled'?: boolean;
  'feature.upsell-sheet.native-terms-and-conditions.enabled'?: boolean;
  'feature.ios-fetch-bridge.enabled'?: boolean;
  'order.shipping.fee'?: string | number;
  'telemed.enabled-states'?: string[];
  'pet-poison-hotline-price'?: string | number;
}

export enum FeatureConfigKeys {
  CANCELLATION_SURVEY_ENABLED = 'cancellation-survey-widget-enabled',
  PET_PORTAL_REDIRECT_ENABLED = 'feature.pet-portal-redirect.enabled',
  HOLIDAY_OFFER_ENABLED = 'feature.holiday-offer.enabled',
  CONTENT_MFE_ENABLED = 'feature.content-mfe.enabled',
  CUSTOMER_QUOTES = 'customer-quotes',
  PET_BIRTHDAY_OFFER_CODE = 'feature.pet-birthday-offer-code',
  HEADER_FOOTER_ENABLED_CHEWY_PORTAL = 'feature.header-footer.enabled.chewy-portal',
  REWARDS_CAP_ENABLED = 'feature.rewards-cap.enabled',
  POSTAL_CHECK_DELIVERY_AREA = 'POSTAL_CHECK_DELIVERY_AREA',
  LOYALTY_PAGE_ANIMATION_TYPE = 'feature.page-animation.type',
  SIGN_UP_NATIVE_SHEETS = 'feature.signup.native-sheets.enabled',
  AUTO_RENEWAL_NATIVE_SHEET = 'feature.auto-renew.native-sheets.enabled',
  EXPIRING_REWARDS_ENABLED = 'feature.expiring-rewards.enabled',
  TERMS_AND_CONDITIONS_NATIVE_SHEET_ENABLED = 'feature.terms-and-conditions.native-sheet.enabled',
  TERMS_AND_CONDITIONS_KIB_SHEET_ENABLED = 'feature.terms-and-conditions.kib-sheet.enabled',
  LOYALTY_SUSPENDED_CANCEL_ENABLED = 'feature.membership.suspended.cancel.enabled',
  DASHBOARD_SAVINGS_AND_REWARDS_NATIVE_SHEETS_ENABLED = 'feature.savings-and-rewards.native-sheets.enabled',
  MANAGE_MEMBERSHIP_NATIVE_SHEET = 'feature.manage-membership.native-sheet.enabled',
  MANAGE_MEMBERSHIP_BPM_DISCLAIMER = 'feature.manage-membership.bpm-disclaimer.enabled',
  ANDROID_VERSION_SUPPORTS_SCROLLING = 'feature.native-sheets.android-version-for-scrolling',
  MANAGE_MEMBERSHIP_NATIVE_SHEET_ANDROID_VERSION = 'feature.native-sheets.android-version-manage-membership-sheet',
  UPDATED_PRICE_ENABLED = 'feature.updated-price.enabled',
  VWO_ENABLED = 'feature.vwo.enabled',
  WIDGET_SCHEDULED_MESSAGES_ENABLED = 'feature.widget-scheduled-messages.enabled',
  WIDGET_SCHEDULED_MESSAGES_CONFIG = 'feature.widget-scheduled-messages.config',
  PET_PROFILE_CAROUSEL_AUTOPLAY_ENABLED = 'feature.pet-profile-carousel.autoplay.enabled',
  HOLIDAY_2025_BORDER_ENABLED = 'feature.holiday-2025-border.enabled',
  UPDATED_THEME_ENABLED = 'feature.updated-theme.enabled',
  UPDATED_THEME_CONFIG = 'feature.updated-theme.config',
  SIGN_UP_PAGE_HOLIDAY_SIGNUP_CONTENT = 'feature.sign-up.holiday-signup.content',
  REVISE_MEMBERSHIP_ORDER_PAYMENT_FLOW_ENABLED = 'feature.revise-membership-order-payment-flow.enabled',
  HOLIDAY_GREETING_NATIVE_SHEET_ENABLED = 'feature.holiday-greeting.native-sheet.enabled',
  MEMBERSHIP_DASHBOARD_V3 = 'feature.membership-dashboard.v3.enabled',
  CMS_ZONE_LIGHT_MODE_LOCK_WORKAROUND_ENABLED = 'feature.cms-zone-light-mode-lock-workaround.enabled',
  UPSELL_STEPPED_TERMS_AND_CONDITIONS_ENABLED = 'feature.upsell-sheet.stepped-terms-and-conditions.enabled',
  UPSELL_APPLE_PAY_AND_GOOGLE_PAY_DISABLED = 'feature.upsell-sheet.apple-pay-and-google-pay.disabled',
  UPSELL_NATIVE_TERMS_AND_CONDITIONS_ENABLED = 'feature.upsell-sheet.native-terms-and-conditions.enabled',
  IOS_FETCH_BRIDGE_ENABLED = 'feature.ios-fetch-bridge.enabled',
  SHIPPING_FEE = 'order.shipping.fee',
  TELEMED_ELIGIBLE_STATE_LIST = 'telemed.enabled-states',
  PET_POISON_HOTLINE_PRICE = 'pet-poison-hotline-price',
}
